import { Root as ScrollAreaRoot } from '@radix-ui/react-scroll-area';
import clsx from 'clsx';
import type {
    ButtonHTMLAttributes,
    CSSProperties,
    FunctionComponent,
    HTMLAttributes,
    KeyboardEvent,
    ReactNode,
} from 'react';
import { createContext, forwardRef, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';

import { Button, type ButtonProps } from '../button/Button';
import { RecessedSurfaceLayers } from '../recessed-surface/RecessedSurface';
import { StyledScrollAreaScrollbar, StyledScrollAreaThumb, StyledScrollAreaViewport } from '../scroll-area/ScrollArea';
import { HIDDEN_SCROLLBAR_CLASS_NAME } from '../scroll-fade/ScrollFade';
import { SkeletonBase } from '../skeleton/Skeleton';
import { TableZoomScope, useTableZoom } from './TableZoomContext';
import { TableZoomControl } from './TableZoomControl';
import { useTableHorizontalScroll } from './use-table-horizontal-scroll';
import { useTableZoomGesture } from './use-table-zoom-gesture';

export type TableColumnWidth = string;
type TableContainerInset = 'comfortable' | 'compact' | 'default' | 'none';
type TableSize = 'default' | 'small';

type TableRowContextValue = {
    readonly isActive: boolean;
    readonly isSelected: boolean;
};

const TableRowContext = createContext<TableRowContextValue>({ isActive: false, isSelected: false });

export const useTableRowContext = (): TableRowContextValue => useContext(TableRowContext);

const tableContainerInsetClass: Record<TableContainerInset, string> = {
    comfortable: 'px-[calc(var(--spacing-s)+var(--spacing-m)-var(--spacing-xxs))] py-s',
    compact: 'p-xxs',
    default: 'p-s',
    none: 'p-0',
};

const calculateLevelOffset = (level: number): string => {
    switch (level) {
        case 0:
            return '0.4rem';
        case 1:
            return '3.4rem';
        case 2:
            return '6.4rem';
        case 3:
            return '9.4rem';
    }

    return '0';
};

type TableContainerProps = HTMLAttributes<HTMLDivElement> & {
    readonly $flushBlockEnd?: boolean;
    readonly $inset?: TableContainerInset;
    // Opts the container's grid into the app zoom level and mounts the hover zoom control.
    readonly $zoomable?: boolean;
};

export const TableContainer: FunctionComponent<TableContainerProps> = ({
    $flushBlockEnd = false,
    $inset = 'default',
    $zoomable = false,
    children,
    className,
    ...props
}) => (
    <TableZoomScope enabled={$zoomable}>
        <div
            className={clsx(
                'group/table-container relative flex flex-col h-full w-full bg-surface-canvas rounded-xxl font-sans',
                tableContainerInsetClass[$inset],
                $flushBlockEnd && 'pb-0',
                className,
            )}
            {...props}
        >
            {children}
            {$zoomable ? <TableZoomControl /> : null}
        </div>
    </TableZoomScope>
);

type TableGridProps = HTMLAttributes<HTMLDivElement> & {
    // Columns wider than the container scroll horizontally with an edge fade instead of being clipped.
    // Inline-size containment keeps the column minimums from inflating ancestors that size to content.
    // Set to false for grids that manage their own overflow.
    readonly $horizontalScroll?: boolean;
    // Shows the ScrollArea scrollbar under the grid while it overflows instead of hiding it. Only for grids
    // that grow with their rows: Radix wraps the content in a display: table box, so a body that scrolls
    // vertically gets no height to fill.
    readonly $showScrollbar?: boolean;
};

const TABLE_GRID_CLASS_NAME = 'grid grid-cols-[1fr] w-full min-w-0';
const TABLE_GRID_FOCUS_RING_CLASS_NAME =
    'focus-visible:outline focus-visible:outline-1 focus-visible:outline-focus-ring';

export const TableGrid: FunctionComponent<TableGridProps> = ({
    $horizontalScroll = true,
    $showScrollbar = false,
    children,
    className,
    onKeyDown,
    style,
    tabIndex,
    ...props
}) => {
    const horizontalScroll = useTableHorizontalScroll({ disabled: !$horizontalScroll });
    const { zoom } = useTableZoom();
    const zoomGestureRef = useTableZoomGesture();
    const { viewportRef } = horizontalScroll;
    const setViewportRef = useCallback(
        (node: HTMLDivElement | null): void => {
            viewportRef(node);
            zoomGestureRef(node);
        },
        [viewportRef, zoomGestureRef],
    );
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (!horizontalScroll.onKeyDown(event)) onKeyDown?.(event);
    };
    const scrollRegionProps = {
        'data-overflow-left': $horizontalScroll ? horizontalScroll.overflowEdges.start : undefined,
        'data-overflow-right': $horizontalScroll ? horizontalScroll.overflowEdges.end : undefined,
        'data-zoom': zoom,
        ref: setViewportRef,
        style: { ...style, ...horizontalScroll.style },
        tabIndex: horizontalScroll.isOverflowing ? 0 : tabIndex,
    };
    const grid =
        $horizontalScroll && $showScrollbar ? (
            <ScrollAreaRoot className="w-full min-w-0 contain-inline-size" type="auto">
                <StyledScrollAreaViewport
                    {...props}
                    {...scrollRegionProps}
                    className={clsx(
                        // Overrides the viewport's outline-none, which would otherwise hide the focus ring.
                        'focus-visible:outline-solid',
                        TABLE_GRID_FOCUS_RING_CLASS_NAME,
                        horizontalScroll.isOverflowing && 'pb-s',
                        className,
                    )}
                    onKeyDown={handleKeyDown}
                >
                    <div className={TABLE_GRID_CLASS_NAME}>{children}</div>
                </StyledScrollAreaViewport>
                <StyledScrollAreaScrollbar data-testid="table-horizontal-scrollbar" orientation="horizontal">
                    <StyledScrollAreaThumb />
                </StyledScrollAreaScrollbar>
            </ScrollAreaRoot>
        ) : (
            // biome-ignore lint/a11y/noStaticElementInteractions: consumers pass role="grid"; the handler only scrolls the focused viewport.
            <div
                {...props}
                {...scrollRegionProps}
                className={clsx(
                    TABLE_GRID_CLASS_NAME,
                    $horizontalScroll
                        ? clsx(
                              'contain-inline-size overflow-x-auto overflow-y-hidden',
                              TABLE_GRID_FOCUS_RING_CLASS_NAME,
                              HIDDEN_SCROLLBAR_CLASS_NAME,
                          )
                        : 'overflow-hidden',
                    className,
                )}
                onKeyDown={handleKeyDown}
            >
                {children}
            </div>
        );

    if (zoom === 1) return grid;

    return <TableZoomViewport zoom={zoom}>{grid}</TableZoomViewport>;
};

// CSS zoom scales the grid's layout box, so a zoomed grid that fills its parent overshoots it and
// percentages inside the zoomed box already resolve in zoomed units. The viewport keeps the unzoomed
// footprint, measures it, and gives the zoomed child an explicit box of footprint / zoom so the grid
// fills the visible area exactly at any level.
const TableZoomViewport: FunctionComponent<{ readonly children: ReactNode; readonly zoom: number }> = ({
    children,
    zoom,
}) => {
    const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
    const [size, setSize] = useState<{ readonly height: number; readonly width: number } | null>(null);

    useLayoutEffect(() => {
        if (viewport === null) return;

        const measure = (): void => {
            const { height, width } = viewport.getBoundingClientRect();
            setSize((current) =>
                current !== null && current.height === height && current.width === width ? current : { height, width },
            );
        };

        measure();

        if (typeof ResizeObserver === 'undefined') return;

        const observer = new ResizeObserver(measure);
        observer.observe(viewport);

        return () => observer.disconnect();
    }, [viewport]);

    return (
        <div
            ref={setViewport}
            className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
            data-zoom-wrapper={zoom}
        >
            <div
                className="flex min-h-0 flex-col"
                style={
                    size === null
                        ? { zoom }
                        : { height: `${size.height / zoom}px`, width: `${size.width / zoom}px`, zoom }
                }
            >
                {children}
            </div>
        </div>
    );
};

export const TableHeader: FunctionComponent<
    HTMLAttributes<HTMLDivElement> & { readonly $columnWidths?: ReadonlyArray<TableColumnWidth> }
> = ({ $columnWidths, className, style, ...props }) => (
    // biome-ignore lint/a11y/useSemanticElements: rows are CSS-grid tracks, not table markup
    // biome-ignore lint/a11y/useFocusableInteractive: cells own the focusable content; the row is never a tab stop
    <div
        className={clsx('grid rounded-xxl text-text-muted text-body-s font-medium gap-s', className)}
        role="row"
        style={{ gridTemplateColumns: $columnWidths?.join(' '), ...style }}
        {...props}
    />
);

export const TableHeaderCell: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    // biome-ignore lint/a11y/useSemanticElements: header cells are CSS-grid tracks, not table markup
    // biome-ignore lint/a11y/useFocusableInteractive: header cells hold static labels, not tab stops
    <div className={clsx('flex items-center text-left py-m px-l', className)} role="columnheader" {...props} />
);

export const TableBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={clsx('grid grid-cols-[1fr] h-full overflow-auto', HIDDEN_SCROLLBAR_CLASS_NAME, className)}
        {...props}
    />
));

const tableRowSizeClass = (
    size: TableSize,
    isCollapsed: boolean,
    startsVisibleGroup: boolean,
    autoHeight: boolean,
): string | false => {
    if (size !== 'small') {
        return false;
    }

    if (isCollapsed) {
        return '!mt-0 max-h-0 -translate-y-[2px] overflow-hidden invisible opacity-0 pointer-events-none !rounded-none';
    }

    if (autoHeight) {
        return clsx(
            startsVisibleGroup ? '!mt-0' : '!mt-s',
            'max-h-none translate-y-0 visible opacity-100 !rounded-none',
        );
    }

    return clsx(startsVisibleGroup ? '!mt-0' : '!mt-s', 'max-h-[44px] translate-y-0 visible opacity-100 !rounded-none');
};

type TableRowProps = HTMLAttributes<HTMLElement> & {
    readonly $animateLayout?: boolean;
    readonly $autoHeight?: boolean;
    readonly $collapsed?: boolean;
    readonly $columnWidths?: ReadonlyArray<TableColumnWidth>;
    readonly $isSelectable?: boolean;
    readonly $isSelected?: boolean;
    readonly $size?: TableSize;
    readonly $startsVisibleGroup?: boolean;
};

export const TableRow: FunctionComponent<TableRowProps> = ({
    $animateLayout = true,
    $autoHeight = false,
    $collapsed = false,
    $columnWidths,
    $isSelectable = false,
    $isSelected = false,
    $size = 'default',
    $startsVisibleGroup = true,
    children,
    className,
    style,
    ...props
}) => {
    const rowContext = useMemo(() => ({ isActive: false, isSelected: $isSelected }), [$isSelected]);

    return (
        // biome-ignore lint/a11y/useSemanticElements: rows are CSS-grid tracks, not table markup
        // biome-ignore lint/a11y/useFocusableInteractive: cells own the focusable content; the row itself is never a tab stop
        <article
            className={clsx(
                'group/recessed-surface isolate grid rounded-lg text-text-content-subtle gap-s mt-s relative bg-surface-raised',
                $animateLayout
                    ? 'transition-[max-height,opacity,transform,background-color] duration-200 ease-in-out'
                    : 'transition-[opacity,transform,background-color] duration-200 ease-out',
                tableRowSizeClass($size, $collapsed, $startsVisibleGroup, $autoHeight),
                className,
            )}
            // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: every Table wraps its rows in role="grid", which requires row children
            role="row"
            style={{ gridTemplateColumns: $columnWidths?.join(' '), ...style }}
            {...props}
            data-is-active={false}
        >
            <RecessedSurfaceLayers
                active={$isSelected}
                hoverActive={$isSelectable && !$isSelected}
                hoverSurfaceShadow="sm"
                hoverSurfaceTone="gray"
                pocketClassName="-z-10"
                pocketTone="orange"
                radius="md"
                surfaceClassName="-z-10"
                surfaceShadow="sm"
                surfaceTone="gray"
                transition="none"
            />
            <TableRowContext.Provider value={rowContext}>{children}</TableRowContext.Provider>
        </article>
    );
};

const tableCellSizeClass: Record<TableSize, string> = {
    default: 'min-h-[48px] py-m px-l',
    small: 'min-h-[30px] items-center px-0 py-xxs',
};

type TableCellProps = HTMLAttributes<HTMLDivElement> & {
    readonly $size?: TableSize;
};

export const TableCell: FunctionComponent<TableCellProps> = ({ $size = 'default', className, ...props }) => (
    // biome-ignore lint/a11y/useSemanticElements: cells are CSS-grid tracks, not table markup
    // biome-ignore lint/a11y/useFocusableInteractive: the controls inside a cell own the focus, not the cell
    <div
        className={clsx('flex items-center text-text-secondary text-xxs', tableCellSizeClass[$size], className)}
        role="gridcell"
        {...props}
    />
);

type TableFullWidthRowProps = HTMLAttributes<HTMLElement> & {
    readonly $autoHeight?: boolean;
    readonly $cellSize?: TableSize;
    readonly $columnWidths?: ReadonlyArray<TableColumnWidth>;
    readonly $size?: TableSize;
    readonly cellClassName?: string;
};

export const TableFullWidthRow: FunctionComponent<TableFullWidthRowProps> = ({
    $autoHeight = false,
    $cellSize = 'default',
    $columnWidths,
    $size = 'default',
    children,
    cellClassName,
    className,
    ...props
}) => (
    <TableRow
        $autoHeight={$autoHeight}
        $columnWidths={$columnWidths}
        $size={$size}
        className={clsx('!rounded-none !bg-transparent', className)}
        {...props}
    >
        <TableCell $size={$cellSize} className={clsx('[grid-column:1/-1]', cellClassName)} role="gridcell">
            {children}
        </TableCell>
    </TableRow>
);

type ExpandableRowProps = HTMLAttributes<HTMLElement> & {
    readonly $columnWidths?: ReadonlyArray<TableColumnWidth>;
    readonly $expanded?: boolean;
    readonly $isActionPending?: boolean;
    readonly $isCut?: boolean;
    readonly $isInSelectedPath?: boolean;
    readonly $isSelected?: boolean;
    readonly $level?: number;
    readonly $roundedBottom?: boolean;
};

export const TableExpandableRow: FunctionComponent<ExpandableRowProps> = ({
    $columnWidths,
    $expanded: _expanded = false,
    $isActionPending = false,
    $isCut = false,
    $isInSelectedPath = false,
    $isSelected = false,
    $level = 0,
    $roundedBottom: _roundedBottom = false,
    children,
    className,
    style,
    ...props
}) => {
    const rowInset = $level > 0 ? `${$level * 3}rem` : undefined;
    const rowContext = useMemo(() => ({ isActive: false, isSelected: $isSelected }), [$isSelected]);

    return (
        <article
            className={clsx(
                'group/recessed-surface isolate grid rounded-lg text-text-content-subtle cursor-pointer gap-s relative bg-surface-raised',
                '[&_[data-show-on-row-hovered]]:invisible hover:[&_[data-show-on-row-hovered]]:visible',
                $isCut && 'border border-dotted border-outline-selected opacity-80',
                $isActionPending && 'pending-copy-border pointer-events-none opacity-70',
                className,
            )}
            style={{
                gridTemplateColumns: $columnWidths?.join(' '),
                marginLeft: rowInset,
                width: rowInset === undefined ? undefined : `calc(100% - ${rowInset})`,
                ...style,
            }}
            {...props}
            data-is-active={false}
            data-selected={$isSelected}
            data-selected-path={$isInSelectedPath}
        >
            <RecessedSurfaceLayers
                active={$isSelected || $isInSelectedPath}
                hoverActive={!$isSelected && !$isInSelectedPath && !$isActionPending}
                hoverSurfaceShadow="sm"
                hoverSurfaceTone="gray"
                pocketClassName="-z-10"
                pocketTone={$isSelected ? 'orange' : 'neutral'}
                radius="md"
                surfaceClassName="-z-10"
                surfaceShadow="sm"
                surfaceTone="gray"
                transition="none"
            />
            <TableRowContext.Provider value={rowContext}>{children}</TableRowContext.Provider>
        </article>
    );
};

type TableTreeLineProps = HTMLAttributes<HTMLDivElement> & {
    readonly $currentIndex: number;
    readonly $horizontalLine: boolean;
    readonly $isFirstInGroup: boolean;
    readonly $isLastInGroup: boolean;
    readonly $level: number;
};

const verticalDottedGradient =
    'repeating-linear-gradient(transparent 0px, transparent 2px, var(--semantic-text-content-subtle) 2px, var(--semantic-text-content-subtle) 6px)';
const horizontalDottedGradient =
    'repeating-linear-gradient(to right, transparent 0px, transparent 2px, var(--semantic-text-content-subtle) 2px, var(--semantic-text-content-subtle) 6px)';
export const TableTreeLine: FunctionComponent<TableTreeLineProps> = ({
    $currentIndex,
    $horizontalLine,
    $isFirstInGroup,
    $isLastInGroup,
    $level,
    className,
    style,
    ...props
}) => (
    <div
        className={clsx('absolute', className)}
        style={{
            background: verticalDottedGradient,
            bottom: $isLastInGroup ? 'calc(50%)' : 0,
            left: `calc(${calculateLevelOffset($level)} + 14px - ${$currentIndex * 3}rem)`,
            top: $isFirstInGroup ? 'calc(20% + 5px - 35px)' : 0,
            width: 1,
            ...style,
        }}
        {...props}
    >
        <span
            style={{
                background: horizontalDottedGradient,
                content: '""',
                height: (!$isFirstInGroup || !$isLastInGroup) && $horizontalLine ? '1px' : '0px',
                left: 0,
                position: 'absolute',
                top: $isLastInGroup ? '100%' : 'calc(50%)',
                transform: 'translateY(-50%)',
                width: !$isFirstInGroup ? '10px' : '0px',
                zIndex: 1,
            }}
        />
    </div>
);

type TableExpandButtonProps = Omit<ButtonProps<'button'>, 'color' | 'iconOnly' | 'size' | 'variant'> & {
    readonly $isExpanded?: boolean;
};

export const TableExpandButton: FunctionComponent<TableExpandButtonProps> = ({
    $isExpanded = false,
    className,
    ...props
}) => (
    <Button
        className={clsx(
            '!rounded-xs !justify-center !mr-s !p-xs',
            $isExpanded ? '[&_svg]:rotate-90' : '[&_svg]:rotate-0',
            '[&_svg]:transition-transform [&_svg]:duration-200 [&_svg]:ease-in-out',
            className,
        )}
        iconOnly={true}
        size="sm"
        variant="tertiary"
        {...props}
    />
);

export const TableNameCell: FunctionComponent<HTMLAttributes<HTMLDivElement> & { readonly $level?: number }> = ({
    $level: _level = 0,
    className,
    ...props
}) => <div className={clsx('flex flex-row items-center', className)} {...props} />;

export const TableNameCellContent: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div className={clsx('flex flex-col items-start gap-s', className)} {...props} />
);

type TableActionButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    readonly children: ReactNode;
    readonly variant?: 'tertiary';
};

export const TableActionButton: FunctionComponent<TableActionButtonProps> = ({
    className,
    color: _color,
    variant: _variant,
    ...props
}) => (
    <Button
        className={clsx(
            '!inline-flex !items-center !justify-center !p-xs',
            '!bg-transparent !border-none !text-text-content-subtle !text-sm !cursor-pointer',
            'hover:!bg-surface-hover hover:!text-text-content-subtle',
            className,
        )}
        size="sm"
        variant="tertiary"
        {...props}
    />
);

export const TableLoadingSpinner: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div
        className={clsx('flex items-center justify-center text-text-content-faint text-base p-xl', className)}
        {...props}
    />
);

export const TableErrorMessage: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
    <div
        className={clsx(
            'flex items-center justify-center',
            'bg-feedback-error-surface border border-border-error-strong rounded-md text-feedback-error-text text-base m-l p-xl',
            className,
        )}
        {...props}
    />
);

type SkeletonRowProps = {
    readonly columnCount: number;
    readonly columnWidths: ReadonlyArray<TableColumnWidth>;
};

export const TableSkeletonRow: FunctionComponent<SkeletonRowProps> = ({ columnCount, columnWidths }) => (
    <TableRow $columnWidths={columnWidths} data-testid="skeleton" role="row">
        {Array.from({ length: columnCount }).map((_, cellIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
            <TableCell key={cellIndex} role="gridcell">
                <SkeletonBase className="h-xl w-[80%]" />
            </TableCell>
        ))}
    </TableRow>
);

type SkeletonHeaderRowProps = {
    readonly columnCount: number;
    readonly columnWidths: ReadonlyArray<TableColumnWidth>;
};

export const TableSkeletonHeaderRow: FunctionComponent<SkeletonHeaderRowProps> = ({ columnCount, columnWidths }) => (
    <TableHeader $columnWidths={columnWidths} data-testid="skeleton" role="row">
        {Array.from({ length: columnCount }).map((_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
            <TableHeaderCell key={index} role="columnheader">
                <SkeletonBase className="h-l w-[70%]" />
            </TableHeaderCell>
        ))}
    </TableHeader>
);

type TableSkeletonProps = {
    readonly 'aria-label'?: string;
    readonly columnWidths: ReadonlyArray<TableColumnWidth>;
    readonly 'data-testid'?: string;
    readonly rowCount?: number;
};

export const TableSkeleton: FunctionComponent<TableSkeletonProps> = ({
    'aria-label': ariaLabel = 'Loading table',
    columnWidths,
    'data-testid': dataTestId = 'table-skeleton',
    rowCount = 5,
}) => {
    const columnCount = columnWidths.length;

    return (
        <TableContainer>
            <TableGrid aria-label={ariaLabel} data-testid={dataTestId} role="grid">
                <TableSkeletonHeaderRow columnCount={columnCount} columnWidths={columnWidths} />
                <TableBody>
                    {Array.from({ length: rowCount }).map((_, rowIndex) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
                        <TableSkeletonRow key={rowIndex} columnCount={columnCount} columnWidths={columnWidths} />
                    ))}
                </TableBody>
            </TableGrid>
        </TableContainer>
    );
};

type TableProps = {
    readonly 'aria-label'?: string;
    readonly children: ReactNode;
    readonly 'data-testid'?: string;
    readonly style?: CSSProperties;
};

export const Table: FunctionComponent<TableProps> = ({
    'aria-label': ariaLabel,
    children,
    'data-testid': dataTestId,
    ...props
}) => (
    <TableContainer {...props}>
        <TableGrid aria-label={ariaLabel} data-testid={dataTestId} role="grid">
            {children}
        </TableGrid>
    </TableContainer>
);
